/**
 * Labstep
 *
 * @module components/ProtocolValue/Form/ShowEdit/ResourceItem/Input
 * @desc Search and select a ResourceItem for a ProtocolValue
 */

import type { IEntitySearchContainerProps } from 'labstep-web/components/Entity/Search/types';
import { ResourceActionCreateForm } from 'labstep-web/components/Resource/Action/Create/Form';
import ResourceItemActionCreate from 'labstep-web/components/ResourceItem/Action/Create';
import { Filter } from 'labstep-web/constants/filters';
import type { IFormShowEditSearchSelectModalProps } from 'labstep-web/core/Form/ShowEdit/SearchSelect/Modal/types';
import type { IFormShowEditSearchSelectContainerProps } from 'labstep-web/core/Form/ShowEdit/SearchSelect/types';
import type { ISearchSelectModalProps } from 'labstep-web/core/Select/Search/Modal/types';
import resourceColumnDefs from 'labstep-web/grid/SearchModal/coldefs/resource';
import columnDefs from 'labstep-web/grid/SearchModal/coldefs/resource-item';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import {
  ResourceItem,
  ResourceItemStatusValues,
} from 'labstep-web/models/resource-item.model';
import { PostFilterOperator } from 'labstep-web/services/postFilter/constants';
import { PostFilterResource } from 'labstep-web/services/postFilter/filters/resource';
import { PostFilterResourceTemplate } from 'labstep-web/services/postFilter/filters/resource/template';
import { PostFilterStatus } from 'labstep-web/services/postFilter/filters/status';
import { selectEntity } from 'labstep-web/state/selectors/entity';
import store from 'labstep-web/state/store';
import type { LabstepReduxState } from 'labstep-web/state/types';
import type React from 'react';
import type { IProtocolValueFormShowEditResourceItemInputProps } from './types';

export const getProtocolValueProps = (
  protocolCondition: ProtocolValue,
  variableTemplate?: ProtocolValue,
  children?: React.ReactNode,
  type?: IProtocolValueFormShowEditResourceItemInputProps['type'],
): IFormShowEditSearchSelectModalProps<ProtocolValue> => ({
  ...getProtocolValueContainerProps(protocolCondition),
  ...getProtocolValueSearchSelectProps(
    variableTemplate,
    children,
    type,
  ),
});

export const getProtocolValueResourceProps = (
  protocolCondition: ProtocolValue,
  variableTemplate?: ProtocolValue,
  children?: React.ReactNode,
): IFormShowEditSearchSelectModalProps<ProtocolValue> => ({
  ...getProtocolValueContainerProps(protocolCondition),
  ...getProtocolValueResourceSearchSelectProps(
    variableTemplate,
    children,
  ),
});

const getProtocolValueSearchProps = (
  variableTemplateGuid?: string,
): Partial<IEntitySearchContainerProps> => {
  const protocolValueEntity: ProtocolValue | null =
    variableTemplateGuid
      ? selectEntity(
          store.getState() as LabstepReduxState,
          ProtocolValue.entityName,
          variableTemplateGuid,
        )
      : null;

  const filter = [
    {
      type: PostFilterOperator.and,
      predicates: [PostFilterStatus.getNode(['available'])],
    },
    {
      type: PostFilterOperator.and,
      predicates: [],
    },
  ];

  if (protocolValueEntity && protocolValueEntity.resource) {
    filter[1].predicates.push(
      PostFilterResource.getNode([protocolValueEntity.resource.guid]),
    );
  }
  if (protocolValueEntity && protocolValueEntity.resource_template) {
    filter[1].predicates.push(
      PostFilterResourceTemplate.getNode([
        protocolValueEntity.resource_template.guid,
      ]),
    );
  }

  return {
    filters: [
      Filter.metadata,
      Filter.resource_metadata,
      Filter.resource_item_status,
      Filter.resource_location,
    ],
    initialSearchParams: {
      filter,
    },
    usePostFilter: true,
  };
};

const getProtocolValueContainerProps = (
  protocolCondition: ProtocolValue,
): Omit<
  IFormShowEditSearchSelectContainerProps<ProtocolValue>,
  'children'
> => ({
  childEntityName: ResourceItem.entityName,
  parentEntity: protocolCondition,
  optimistic: false,
});

const getProtocolValueSearchSelectProps = (
  variableTemplate?: ProtocolValue | null,
  children?: React.ReactNode,
  type?: IProtocolValueFormShowEditResourceItemInputProps['type'],
): Omit<ISearchSelectModalProps, 'onChange' | 'entityName'> & {
  children: React.ReactNode;
} => {
  const resource = variableTemplate?.resource || undefined;
  const resourceTemplate =
    variableTemplate?.resource_template || undefined;
  return {
    children: children || null,
    createAction: (
      <ResourceItemActionCreate
        resource={resource}
        resourceTemplate={!resource ? resourceTemplate : undefined}
        withRedirect={false}
      />
    ),
    creatable: true,
    searchProps: getProtocolValueSearchProps(variableTemplate?.guid),
    actionComponentProps:
      type === 'button'
        ? {
            type: 'button',
            icon: 'search',
            showIcon: true,
            elementProps: {
              size: 'large',
              primary: true,
              basic: true,
              fluid: true,
            },
            text: 'Search for Item',
          }
        : undefined,
    columnDefs: columnDefs(),
  };
};

const getProtocolValueResourceSearchSelectProps = (
  variableTemplate?: ProtocolValue,
  children?: React.ReactNode,
  type?: IProtocolValueFormShowEditResourceItemInputProps['type'],
): Omit<ISearchSelectModalProps, 'onChange' | 'entityName'> & {
  children: React.ReactNode;
} => ({
  children: children || null,
  createAction: (
    <ResourceActionCreateForm
      resourceTemplate={
        variableTemplate?.resource_template || undefined
      }
      actionComponentProps={{ type: 'button' }}
      withRedirect={false}
    />
  ),
  creatable: true,
  // searchProps: getProtocolValueSearchProps(variableTemplateGuid),
  actionComponentProps:
    type === 'button'
      ? {
          type: 'button',
          icon: 'search',
          showIcon: true,
          elementProps: {
            size: 'large',
            primary: true,
            basic: true,
            fluid: true,
          },
          text: 'Search for Item',
        }
      : undefined,
  columnDefs: resourceColumnDefs(),
});

export const getProtocolValueResourceItemStatusSelectProps = (
  resourceItem: ResourceItem,
  onChange: (option: { value: string }) => void,
) => ({
  value: {
    label:
      ResourceItemStatusValues[
        resourceItem.status as keyof typeof ResourceItemStatusValues
      ],
    value: resourceItem.status,
  },
  options: Object.keys(ResourceItemStatusValues).map((status) => ({
    label:
      ResourceItemStatusValues[
        status as keyof typeof ResourceItemStatusValues
      ],
    value: status,
  })),
  onChange,
});
